import { useState, useEffect, useCallback } from "react";
import { 
  Users, Settings, Save, Plus, Trash2, AlertCircle, RefreshCw,
  UserCheck, BarChart3, Target, MapPin, Briefcase, Zap, 
  CheckCircle, History, UserPlus, Percent, ArrowRightLeft
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadAssignmentSettings = () => {
  const { selectedBusinessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");
  
  const [settings, setSettings] = useState({
    assignment_mode: "manual",
    enabled: true,
    default_assignee: null,
    skip_inactive_users: true,
    round_robin_users: [],
    weighted_distribution: {},
    rules: []
  });
  
  const [availableUsers, setAvailableUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({
    name: "",
    rule_type: "source_based",
    enabled: true,
    priority: 0,
    conditions: { sources: [], cities: [], services: [], courses: [] },
    users: []
  });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/lead-assignment/settings?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/lead-assignment/settings`;
      
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSettings(data.settings || {
          assignment_mode: "manual",
          enabled: true,
          default_assignee: null,
          skip_inactive_users: true,
          round_robin_users: [],
          weighted_distribution: {},
          rules: []
        });
        setAvailableUsers(data.available_users || []);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [selectedBusinessId]);

  const fetchLogs = useCallback(async () => {
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/lead-assignment/logs?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/lead-assignment/logs`;
      
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, [selectedBusinessId]);

  const fetchStats = useCallback(async () => {
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/lead-assignment/stats?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/lead-assignment/stats`;
      
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) {
      console.error(e);
    }
  }, [selectedBusinessId]);

  useEffect(() => {
    fetchSettings();
    fetchLogs();
    fetchStats();
  }, [fetchSettings, fetchLogs, fetchStats]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/lead-assignment/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...settings,
          business_id: selectedBusinessId
        })
      });

      if (res.ok) {
        toast.success("Settings saved successfully");
        fetchSettings();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to save settings");
      }
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleModeChange = (mode) => {
    setSettings({ ...settings, assignment_mode: mode });
  };

  const handleToggleUser = (userId) => {
    const current = settings.round_robin_users || [];
    const newList = current.includes(userId)
      ? current.filter(id => id !== userId)
      : [...current, userId];
    setSettings({ ...settings, round_robin_users: newList });
  };

  const handleWeightChange = (userId, weight) => {
    const newWeights = { ...settings.weighted_distribution };
    if (weight <= 0) {
      delete newWeights[userId];
    } else {
      newWeights[userId] = weight;
    }
    setSettings({ ...settings, weighted_distribution: newWeights });
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setNewRule({
      name: "",
      rule_type: "source_based",
      enabled: true,
      priority: settings.rules?.length || 0,
      conditions: { sources: [], cities: [], services: [], courses: [] },
      users: []
    });
    setShowRuleDialog(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setNewRule({ ...rule });
    setShowRuleDialog(true);
  };

  const handleSaveRule = () => {
    const rules = [...(settings.rules || [])];
    
    if (editingRule) {
      const index = rules.findIndex(r => r.rule_id === editingRule.rule_id);
      if (index >= 0) {
        rules[index] = { ...newRule, rule_id: editingRule.rule_id };
      }
    } else {
      rules.push({ ...newRule, rule_id: `rule_${Date.now()}` });
    }
    
    setSettings({ ...settings, rules });
    setShowRuleDialog(false);
  };

  const handleDeleteRule = (ruleId) => {
    if (!confirm("Delete this rule?")) return;
    const rules = settings.rules.filter(r => r.rule_id !== ruleId);
    setSettings({ ...settings, rules });
  };

  const getUserName = (userId) => {
    const user = availableUsers.find(u => u.user_id === userId);
    return user ? user.name : userId;
  };

  const getTotalWeight = () => {
    const weights = settings.weighted_distribution || {};
    return Object.values(weights).reduce((a, b) => a + b, 0);
  };

  const toggleRuleUser = (userId) => {
    const users = newRule.users || [];
    const isSelected = users.includes(userId);
    setNewRule({
      ...newRule,
      users: isSelected ? users.filter(id => id !== userId) : [...users, userId]
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="lead-assignment-settings">
      <PageHeader onSave={handleSaveSettings} saving={saving} />
      
      {stats && <StatsSection stats={stats} />}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 md:w-auto md:inline-flex">
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Assignment Mode</span>
            <span className="sm:hidden">Mode</span>
          </TabsTrigger>
          <TabsTrigger value="rules" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Rules
            {settings.rules?.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">{settings.rules.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Logs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="space-y-6 mt-6">
          <SettingsTab 
            settings={settings} 
            setSettings={setSettings}
            availableUsers={availableUsers}
            handleModeChange={handleModeChange}
            handleToggleUser={handleToggleUser}
            handleWeightChange={handleWeightChange}
            getTotalWeight={getTotalWeight}
          />
        </TabsContent>

        <TabsContent value="rules" className="space-y-6 mt-6">
          <RulesTab 
            rules={settings.rules || []}
            onAddRule={handleAddRule}
            onEditRule={handleEditRule}
            onDeleteRule={handleDeleteRule}
            getUserName={getUserName}
          />
        </TabsContent>

        <TabsContent value="logs" className="space-y-6 mt-6">
          <LogsTab logs={logs} onRefresh={fetchLogs} />
        </TabsContent>
      </Tabs>

      <RuleDialog
        open={showRuleDialog}
        onOpenChange={setShowRuleDialog}
        rule={newRule}
        setRule={setNewRule}
        onSave={handleSaveRule}
        availableUsers={availableUsers}
        toggleUser={toggleRuleUser}
        isEditing={!!editingRule}
      />
    </div>
  );
};

function PageHeader({ onSave, saving }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <UserPlus className="w-8 h-8 text-emerald-600" />
          Lead Assignment Rules
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Configure automatic lead distribution and assignment rules
        </p>
      </div>
      <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
        {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        Save Settings
      </Button>
    </div>
  );
}

function StatsSection({ stats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard label="Total Leads" value={stats.total_leads} icon={Users} color="blue" />
      <StatCard label="Unassigned" value={stats.unassigned_leads} icon={AlertCircle} color="amber" />
      <StatCard label="Assigned Today" value={stats.today_assignments} icon={UserCheck} color="emerald" />
      <StatCard label="This Month" value={stats.month_assignments} icon={BarChart3} color="purple" />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    emerald: "bg-emerald-50 text-emerald-600",
    purple: "bg-purple-50 text-purple-600"
  };
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-slate-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SettingsTab({ settings, setSettings, availableUsers, handleModeChange, handleToggleUser, handleWeightChange, getTotalWeight }) {
  return (
    <>
      <AutoAssignmentCard settings={settings} setSettings={setSettings} availableUsers={availableUsers} />
      <AssignmentModeCard settings={settings} handleModeChange={handleModeChange} />
      
      {settings.assignment_mode === "round_robin" && (
        <RoundRobinCard 
          users={availableUsers} 
          selectedUsers={settings.round_robin_users || []}
          onToggle={handleToggleUser}
        />
      )}
      
      {settings.assignment_mode === "weighted" && (
        <WeightedCard 
          users={availableUsers} 
          weights={settings.weighted_distribution || {}}
          onWeightChange={handleWeightChange}
          totalWeight={getTotalWeight()}
        />
      )}
    </>
  );
}

function AutoAssignmentCard({ settings, setSettings, availableUsers }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-emerald-600" />
          Auto Assignment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div>
            <p className="font-medium">Enable Auto Assignment</p>
            <p className="text-sm text-slate-500">Automatically assign new leads based on rules</p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
            data-testid="enable-auto-assignment"
          />
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div>
            <p className="font-medium">Skip Inactive Users</p>
            <p className="text-sm text-slate-500">Exclude users on leave or marked absent</p>
          </div>
          <Switch
            checked={settings.skip_inactive_users}
            onCheckedChange={(v) => setSettings({ ...settings, skip_inactive_users: v })}
          />
        </div>

        <div>
          <Label className="text-sm font-medium">Default Assignee (Fallback)</Label>
          <p className="text-xs text-slate-500 mb-2">Assign to this user when no rules match</p>
          <Select
            value={settings.default_assignee || "none"}
            onValueChange={(v) => setSettings({ ...settings, default_assignee: v === "none" ? null : v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select default user" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No default (stay unassigned)</SelectItem>
              {availableUsers.map(user => (
                <SelectItem key={user.user_id} value={user.user_id}>
                  {user.name} ({user.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}

function AssignmentModeCard({ settings, handleModeChange }) {
  const modes = [
    { value: "manual", label: "Manual Assignment", icon: UserCheck, description: "Manually select user when creating leads" },
    { value: "round_robin", label: "Round Robin", icon: RefreshCw, description: "Rotate leads equally among selected users" },
    { value: "weighted", label: "Weighted Distribution", icon: Percent, description: "Distribute based on percentage per user" },
    { value: "rule_based", label: "Rule-Based", icon: Target, description: "Auto-assign based on source, city, or service" }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5 text-emerald-600" />
          Assignment Mode
        </CardTitle>
        <CardDescription>Choose how leads are distributed among team members</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {modes.map((mode) => (
            <ModeCard 
              key={mode.value}
              mode={mode}
              isSelected={settings.assignment_mode === mode.value}
              onClick={() => handleModeChange(mode.value)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ModeCard({ mode, isSelected, onClick }) {
  const Icon = mode.icon;
  return (
    <div
      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
        isSelected
          ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20"
          : "border-slate-200 dark:border-slate-700 hover:border-emerald-300"
      }`}
      onClick={onClick}
      data-testid={`mode-${mode.value}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${isSelected ? "bg-emerald-100 dark:bg-emerald-800" : "bg-slate-100 dark:bg-slate-800"}`}>
          <Icon className={`w-5 h-5 ${isSelected ? "text-emerald-600" : "text-slate-500"}`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium">{mode.label}</p>
            {isSelected && <CheckCircle className="w-4 h-4 text-emerald-600" />}
          </div>
          <p className="text-sm text-slate-500 mt-1">{mode.description}</p>
        </div>
      </div>
    </div>
  );
}

function RoundRobinCard({ users, selectedUsers, onToggle }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5 text-emerald-600" />
          Round Robin Users
        </CardTitle>
        <CardDescription>Select users to participate in round robin rotation</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {users.map(user => (
            <UserToggleCard 
              key={user.user_id}
              user={user}
              isSelected={selectedUsers.includes(user.user_id)}
              onToggle={() => onToggle(user.user_id)}
            />
          ))}
        </div>
        {selectedUsers.length > 0 && (
          <p className="text-sm text-emerald-600 mt-3">
            <CheckCircle className="w-4 h-4 inline mr-1" />
            {selectedUsers.length} users in rotation
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function UserToggleCard({ user, isSelected, onToggle }) {
  return (
    <div
      className={`p-3 rounded-lg border cursor-pointer transition-all ${
        isSelected
          ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20"
          : "border-slate-200 dark:border-slate-700 hover:border-emerald-300"
      }`}
      onClick={onToggle}
    >
      <div className="flex items-center gap-3">
        <Checkbox checked={isSelected} />
        <div>
          <p className="font-medium">{user.name}</p>
          <p className="text-xs text-slate-500">{user.role}</p>
        </div>
      </div>
    </div>
  );
}

function WeightedCard({ users, weights, onWeightChange, totalWeight }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Percent className="w-5 h-5 text-emerald-600" />
          Weighted Distribution
        </CardTitle>
        <CardDescription>Set percentage for each user (should total 100%)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {users.map(user => (
          <WeightSlider 
            key={user.user_id}
            user={user}
            weight={weights[user.user_id] || 0}
            onChange={(v) => onWeightChange(user.user_id, v)}
          />
        ))}
        <div className={`p-3 rounded-lg ${totalWeight === 100 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          <p className="font-medium flex items-center gap-2">
            {totalWeight === 100 ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            Total: {totalWeight}%
            {totalWeight !== 100 && " (should be 100%)"}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function WeightSlider({ user, weight, onChange }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-40">
        <p className="font-medium truncate">{user.name}</p>
        <p className="text-xs text-slate-500">{user.role}</p>
      </div>
      <div className="flex-1">
        <Slider
          value={[weight]}
          onValueChange={(v) => onChange(v[0])}
          max={100}
          step={5}
        />
      </div>
      <div className="w-20 text-right">
        <Badge variant={weight > 0 ? "default" : "secondary"}>
          {weight}%
        </Badge>
      </div>
    </div>
  );
}

function RulesTab({ rules, onAddRule, onEditRule, onDeleteRule, getUserName }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-emerald-600" />
            Assignment Rules
          </CardTitle>
          <CardDescription>Create rules for source, city, or service-based assignment</CardDescription>
        </div>
        <Button onClick={onAddRule} data-testid="add-rule-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Rule
        </Button>
      </CardHeader>
      <CardContent>
        {rules.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Target className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>No rules configured</p>
            <p className="text-sm">Add rules to auto-assign leads based on source, city, or service</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map(rule => (
              <RuleCard 
                key={rule.rule_id}
                rule={rule}
                onEdit={() => onEditRule(rule)}
                onDelete={() => onDeleteRule(rule.rule_id)}
                getUserName={getUserName}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RuleCard({ rule, onEdit, onDelete, getUserName }) {
  const getIcon = () => {
    if (rule.rule_type === "source_based") return <Zap className="w-4 h-4 text-emerald-600" />;
    if (rule.rule_type === "city_based") return <MapPin className="w-4 h-4 text-blue-600" />;
    return <Briefcase className="w-4 h-4 text-purple-600" />;
  };

  const getConditionValues = () => {
    const conditions = rule.conditions || {};
    return [...(conditions.sources || []), ...(conditions.cities || []), ...(conditions.services || []), ...(conditions.courses || [])];
  };

  return (
    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${rule.enabled ? "bg-emerald-100" : "bg-slate-200"}`}>
            {getIcon()}
          </div>
          <div>
            <p className="font-medium flex items-center gap-2">
              {rule.name}
              <Badge variant={rule.enabled ? "default" : "secondary"}>
                {rule.enabled ? "Active" : "Disabled"}
              </Badge>
            </p>
            <p className="text-sm text-slate-500 capitalize">{rule.rule_type.replace("_", " ")}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {getConditionValues().map((val, i) => (
                <Badge key={i} variant="outline" className="text-xs">{val}</Badge>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Assigns to: {rule.users?.map(u => getUserName(u)).join(", ") || "No users"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onEdit}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={onDelete}>
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function LogsTab({ logs, onRefresh }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-emerald-600" />
            Assignment Logs
          </CardTitle>
          <CardDescription>Recent lead assignment history</CardDescription>
        </div>
        <Button variant="outline" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>No assignment logs yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.slice(0, 50).map(log => (
                  <LogRow key={log.log_id} log={log} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LogRow({ log }) {
  return (
    <TableRow>
      <TableCell className="font-medium">{log.lead_name}</TableCell>
      <TableCell>
        <Badge variant="outline">{log.source || "N/A"}</Badge>
      </TableCell>
      <TableCell>
        <span className="text-sm capitalize">{log.assignment_mode?.replace("_", " ")}</span>
        {log.rule_matched && <p className="text-xs text-slate-500">{log.rule_matched}</p>}
      </TableCell>
      <TableCell>{log.assigned_to_name}</TableCell>
      <TableCell className="text-sm text-slate-500">
        {new Date(log.created_at).toLocaleString()}
      </TableCell>
    </TableRow>
  );
}

function RuleDialog({ open, onOpenChange, rule, setRule, onSave, availableUsers, toggleUser, isEditing }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-emerald-600" />
            {isEditing ? "Edit Rule" : "Add Assignment Rule"}
          </DialogTitle>
          <DialogDescription>
            Create a rule to auto-assign leads based on conditions
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label>Rule Name *</Label>
            <Input
              value={rule.name}
              onChange={(e) => setRule({ ...rule, name: e.target.value })}
              placeholder="e.g., Facebook Leads to Sales Team"
            />
          </div>

          <div>
            <Label>Rule Type</Label>
            <Select
              value={rule.rule_type}
              onValueChange={(v) => setRule({ ...rule, rule_type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="source_based">Source-Based (Facebook, Google, etc.)</SelectItem>
                <SelectItem value="city_based">City-Based</SelectItem>
                <SelectItem value="service_based">Service-Based</SelectItem>
                <SelectItem value="course_based">Course-Based (matches Course Interested)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <RuleConditionInput rule={rule} setRule={setRule} />

          <div>
            <Label>Assign To Users</Label>
            <p className="text-xs text-slate-500 mb-2">Select users for this rule (uses round robin)</p>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-2">
              {availableUsers.map(user => (
                <RuleUserCheckbox 
                  key={user.user_id}
                  user={user}
                  isSelected={(rule.users || []).includes(user.user_id)}
                  onToggle={() => toggleUser(user.user_id)}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label>Rule Enabled</Label>
            <Switch
              checked={rule.enabled}
              onCheckedChange={(v) => setRule({ ...rule, enabled: v })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={onSave}
            disabled={!rule.name || !rule.users?.length}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isEditing ? "Update Rule" : "Add Rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RuleConditionInput({ rule, setRule }) {
  const handleChange = (field, value) => {
    const values = value.split(",").map(s => s.trim()).filter(Boolean);
    setRule({
      ...rule,
      conditions: { ...rule.conditions, [field]: values }
    });
  };

  if (rule.rule_type === "source_based") {
    return (
      <div>
        <Label>Lead Sources (comma-separated)</Label>
        <Input
          value={(rule.conditions?.sources || []).join(", ")}
          onChange={(e) => handleChange("sources", e.target.value)}
          placeholder="Facebook Ads, Google Ads, Justdial"
        />
      </div>
    );
  }

  if (rule.rule_type === "city_based") {
    return (
      <div>
        <Label>Cities (comma-separated)</Label>
        <Input
          value={(rule.conditions?.cities || []).join(", ")}
          onChange={(e) => handleChange("cities", e.target.value)}
          placeholder="Delhi, Mumbai, Bangalore"
        />
      </div>
    );
  }

  if (rule.rule_type === "course_based") {
    return (
      <div>
        <Label>Course Keywords (comma-separated)</Label>
        <p className="text-xs text-slate-500 mb-2">Matches against the lead's "Course Interested" field (case-insensitive substring). Example: <code>BBA, MBA, BCA, MCA</code> matches BBA, MBA, BCA, MCA leads.</p>
        <Input
          value={(rule.conditions?.courses || []).join(", ")}
          onChange={(e) => handleChange("courses", e.target.value)}
          placeholder="BBA, MBA, BCA, MCA"
        />
      </div>
    );
  }

  return (
    <div>
      <Label>Services (comma-separated)</Label>
      <Input
        value={(rule.conditions?.services || []).join(", ")}
        onChange={(e) => handleChange("services", e.target.value)}
        placeholder="SEO, Web Development, Digital Marketing"
      />
    </div>
  );
}

function RuleUserCheckbox({ user, isSelected, onToggle }) {
  return (
    <div
      className={`p-2 rounded border cursor-pointer ${
        isSelected ? "border-emerald-500 bg-emerald-50" : "border-slate-200"
      }`}
      onClick={onToggle}
    >
      <div className="flex items-center gap-2">
        <Checkbox checked={isSelected} />
        <span className="text-sm">{user.name}</span>
      </div>
    </div>
  );
}

export default LeadAssignmentSettings;
